import React, { useState, useRef, useEffect, useCallback } from 'react';
import { useAuth } from '../../auth/AuthContext';
import { Link, useSearchParams } from 'react-router-dom';
import apiService from '../../services/api';
import './DexaScans.css';

// ============================================================
// WOMEN'S-HEALTH-OPTIMIZED DEXA RANGES
// ============================================================

const OPTIMAL_RANGES = {
  // Body Composition
  'Total Body % Fat':      { min: 20, max: 30, concern_high: 40, category: 'body_composition' },
  'Fat Mass/Height\u00B2': { max: 9, concern_high: 13, category: 'body_composition' },

  // Bone Health
  'T-Score':    { min: -1.0, concern_low: -2.5, category: 'bone_health' },
  'Z-Score':    { min: -2.0, concern_low: -3.0, category: 'bone_health' },
  'Total BMD':  { min: 1.0, category: 'bone_health' },

  // Visceral Fat
  'Android/Gynoid Ratio':        { max: 0.8, concern_high: 1.0, category: 'visceral_fat' },
  'VAT Area':                    { max: 100, concern_high: 160, category: 'visceral_fat' },
  'VAT Mass':                    { max: 500, concern_high: 1000, category: 'visceral_fat' },
  'Trunk/Limb Fat Mass Ratio':   { max: 1.0, concern_high: 1.5, category: 'visceral_fat' },
  '% Fat Trunk/% Fat Legs':      { max: 1.0, concern_high: 1.2, category: 'visceral_fat' },

  // Lean Mass
  'Lean/Height\u00B2':          { min: 14.5, concern_low: 13.0, category: 'lean_mass' },
  'Appen. Lean/Height\u00B2':   { min: 5.5, concern_low: 5.0, category: 'lean_mass' },

  // Derived
  'BMI': { min: 18.5, max: 24.9, concern_high: 30, concern_low: 16, category: 'derived' },
};

const CATEGORY_INFO = {
  body_composition: {
    name: 'Body Composition',
    context: 'Women naturally carry more body fat than men due to estrogen. Healthy body fat for women is 20\u201330%. During menopause, fat redistribution shifts toward the trunk, increasing metabolic risk even without weight gain.',
  },
  bone_health: {
    name: 'Bone Health',
    context: 'Women lose up to 20% of bone density in the 5\u20137 years after menopause due to estrogen loss. A T-score above -1.0 is normal; below -2.5 indicates osteoporosis. Weight-bearing exercise and adequate calcium/vitamin D are protective.',
  },
  visceral_fat: {
    name: 'Visceral Fat',
    context: 'Visceral adipose tissue (VAT) is the metabolically dangerous fat around organs. Post-menopause, women\'s VAT increases significantly. Android/gynoid ratio and VAT area are strong predictors of cardiometabolic risk in women.',
  },
  lean_mass: {
    name: 'Lean Mass',
    context: 'Sarcopenia (low muscle mass) affects women earlier than men and accelerates after menopause. Appendicular lean mass index below 5.5 kg/m\u00B2 indicates sarcopenia risk. Resistance training is the most effective intervention.',
  },
  derived: {
    name: 'Derived Metrics',
    context: 'BMI is a basic screening tool. DEXA-derived body composition provides much more accurate assessment of health risk than BMI alone, especially for women whose body composition changes significantly across life stages.',
  },
};

const CATEGORY_ORDER = [
  'body_composition', 'bone_health', 'visceral_fat', 'lean_mass', 'derived',
];

const BODY_REGIONS = ['L Arm', 'R Arm', 'Trunk', 'L Leg', 'R Leg', 'Total'];
const BODY_FIELDS = [
  { key: 'fat_mass', label: 'Fat Mass (g)' },
  { key: 'lean_bmc', label: 'Lean+BMC (g)' },
  { key: 'total_mass', label: 'Total Mass (g)' },
  { key: 'pct_fat', label: '% Fat' },
];

// ============================================================
// SCORING (same pattern as LabResults)
// ============================================================

function scoreTest(testName, value) {
  const range = OPTIMAL_RANGES[testName];
  if (!range || value === null || value === undefined || isNaN(value)) return null;
  const { min, max, concern_high, concern_low } = range;
  if (min === undefined && max === undefined) return null;

  const aboveMin = min === undefined || value >= min;
  const belowMax = max === undefined || value <= max;
  if (aboveMin && belowMax) return 95;

  if (concern_high !== undefined && value >= concern_high) return 25;
  if (concern_low !== undefined && value <= concern_low) return 25;

  if (max !== undefined && value > max) {
    if (concern_high !== undefined) {
      const ratio = Math.min((value - max) / (concern_high - max), 1);
      return Math.round(95 - ratio * 70);
    }
    return 60;
  }
  if (min !== undefined && value < min) {
    if (concern_low !== undefined) {
      const ratio = Math.min((min - value) / (min - concern_low), 1);
      return Math.round(95 - ratio * 70);
    }
    return 60;
  }
  return 60;
}

function getStatus(score) {
  if (score === null) return 'unknown';
  if (score >= 80) return 'optimal';
  if (score >= 50) return 'borderline';
  return 'concerning';
}

function getOptimalDisplay(testName) {
  const r = OPTIMAL_RANGES[testName];
  if (!r) return '\u2014';
  if (r.min !== undefined && r.max !== undefined) return `${r.min}\u2013${r.max}`;
  if (r.max !== undefined) return `< ${r.max}`;
  if (r.min !== undefined) return `> ${r.min}`;
  return '\u2014';
}

function calculateAllScores(adiposeIndices, boneData, leanIndices, bmi) {
  // Build flat list of tests from form sections
  const tests = [];

  // Adipose
  if (adiposeIndices.total_body_pct_fat) tests.push({ name: 'Total Body % Fat', value: adiposeIndices.total_body_pct_fat, category: 'body_composition' });
  if (adiposeIndices.fat_mass_height_sq) tests.push({ name: 'Fat Mass/Height\u00B2', value: adiposeIndices.fat_mass_height_sq, category: 'body_composition' });
  if (adiposeIndices.android_gynoid_ratio) tests.push({ name: 'Android/Gynoid Ratio', value: adiposeIndices.android_gynoid_ratio, category: 'visceral_fat' });
  if (adiposeIndices.pct_fat_trunk_legs) tests.push({ name: '% Fat Trunk/% Fat Legs', value: adiposeIndices.pct_fat_trunk_legs, category: 'visceral_fat' });
  if (adiposeIndices.trunk_limb_ratio) tests.push({ name: 'Trunk/Limb Fat Mass Ratio', value: adiposeIndices.trunk_limb_ratio, category: 'visceral_fat' });
  if (adiposeIndices.vat_mass) tests.push({ name: 'VAT Mass', value: adiposeIndices.vat_mass, category: 'visceral_fat' });
  if (adiposeIndices.vat_area) tests.push({ name: 'VAT Area', value: adiposeIndices.vat_area, category: 'visceral_fat' });

  // Bone
  if (boneData.total_bmd) tests.push({ name: 'Total BMD', value: boneData.total_bmd, category: 'bone_health' });
  if (boneData.t_score) tests.push({ name: 'T-Score', value: boneData.t_score, category: 'bone_health' });
  if (boneData.z_score) tests.push({ name: 'Z-Score', value: boneData.z_score, category: 'bone_health' });

  // Lean
  if (leanIndices.lean_height_sq) tests.push({ name: 'Lean/Height\u00B2', value: leanIndices.lean_height_sq, category: 'lean_mass' });
  if (leanIndices.appen_lean_height_sq) tests.push({ name: 'Appen. Lean/Height\u00B2', value: leanIndices.appen_lean_height_sq, category: 'lean_mass' });

  // Derived
  if (bmi) tests.push({ name: 'BMI', value: bmi, category: 'derived' });

  // Score them
  const cats = {};
  for (const test of tests) {
    const cat = test.category;
    if (!cats[cat]) cats[cat] = { testScores: [] };
    const score = scoreTest(test.name, test.value);
    cats[cat].testScores.push({ ...test, score, status: getStatus(score) });
  }

  const result = {};
  let totalScore = 0;
  let scoredCount = 0;
  for (const cat of CATEGORY_ORDER) {
    if (!cats[cat]) continue;
    const scored = cats[cat].testScores.filter((t) => t.score !== null);
    const avg = scored.length > 0
      ? Math.round(scored.reduce((s, t) => s + t.score, 0) / scored.length)
      : null;
    result[cat] = {
      score: avg,
      status: getStatus(avg),
      tests: cats[cat].testScores,
    };
    if (avg !== null) {
      totalScore += avg;
      scoredCount += 1;
    }
  }

  const overall = scoredCount > 0 ? Math.round(totalScore / scoredCount) : null;
  return { categories: result, overall, overallStatus: getStatus(overall) };
}

// ============================================================
// COMPONENT
// ============================================================

function DexaScans() {
  const { user } = useAuth();
  const [searchParams] = useSearchParams();
  const fromOnboarding = searchParams.get('from') === 'onboarding';
  const [view, setView] = useState('upload');
  const [file, setFile] = useState(null);
  const [isDragging, setIsDragging] = useState(false);
  const [error, setError] = useState('');
  const fileInputRef = useRef(null);
  const [patientInfo, setPatientInfo] = useState({});
  const [bodyComposition, setBodyComposition] = useState(() => {
    const init = {};
    BODY_REGIONS.forEach((r) => {
      init[r] = { fat_mass: '', lean_bmc: '', total_mass: '', pct_fat: '' };
    });
    return init;
  });
  const [boneDensity, setBoneDensity] = useState({ total_bmd: '', t_score: '', z_score: '' });
  const [adiposeIndices, setAdiposeIndices] = useState({
    total_body_pct_fat: '', fat_mass_height_sq: '',
    android_gynoid_ratio: '', pct_fat_trunk_legs: '',
    trunk_limb_ratio: '', vat_mass: '', vat_volume: '', vat_area: '',
  });
  const [leanIndices, setLeanIndices] = useState({ lean_height_sq: '', appen_lean_height_sq: '' });
  const [scores, setScores] = useState(null);
  const [savedResults, setSavedResults] = useState([]);
  const [saveStatus, setSaveStatus] = useState('');

  // ---- Fetch saved results ----

  const fetchSavedResults = useCallback(async () => {
    try {
      const res = await apiService.dexaScans.getResults();
      setSavedResults(res.data || []);
    } catch {
      // silently fail
    }
  }, []);

  useEffect(() => {
    if (user) fetchSavedResults();
  }, [user, fetchSavedResults]);

  // ---- Upload handlers ----

  const handleFileSelect = (e) => {
    const f = e.target.files[0];
    if (f && f.type === 'application/pdf') {
      setFile(f);
      setError('');
    } else {
      setError('Please select a PDF file.');
    }
  };

  const handleDragOver = (e) => { e.preventDefault(); setIsDragging(true); };
  const handleDragLeave = () => setIsDragging(false);
  const handleDrop = (e) => {
    e.preventDefault();
    setIsDragging(false);
    const f = e.dataTransfer.files[0];
    if (f && f.type === 'application/pdf') {
      setFile(f);
      setError('');
    } else {
      setError('Please drop a PDF file.');
    }
  };

  const handleExtract = async () => {
    if (!file) return;
    setView('extracting');
    setError('');
    try {
      const res = await apiService.dexaScans.extractData(file);
      const data = res.data;
      // Populate form state from extracted data
      if (data.patient_info) setPatientInfo(data.patient_info);
      if (data.body_composition) {
        const bc = {};
        BODY_REGIONS.forEach((r) => {
          const saved = data.body_composition[r] || {};
          bc[r] = {
            fat_mass: saved.fat_mass ?? '',
            lean_bmc: saved.lean_bmc ?? '',
            total_mass: saved.total_mass ?? '',
            pct_fat: saved.pct_fat ?? '',
          };
        });
        setBodyComposition(bc);
      }
      if (data.bone_density) {
        setBoneDensity({
          total_bmd: data.bone_density.total_bmd ?? '',
          t_score: data.bone_density.t_score ?? '',
          z_score: data.bone_density.z_score ?? '',
        });
      }
      if (data.adipose_indices) {
        const ai = {};
        for (const k of Object.keys(adiposeIndices)) {
          ai[k] = data.adipose_indices[k] ?? '';
        }
        setAdiposeIndices(ai);
      }
      if (data.lean_indices) {
        setLeanIndices({
          lean_height_sq: data.lean_indices.lean_height_sq ?? '',
          appen_lean_height_sq: data.lean_indices.appen_lean_height_sq ?? '',
        });
      }
      setView('form');
    } catch (err) {
      const detail = err.response?.data?.detail || 'Failed to extract DEXA data. You can enter values manually.';
      setError(detail);
      setView('upload');
    }
  };

  const handleManualEntry = () => {
    setView('form');
  };

  // ---- Helpers ----

  const parseNum = (v) => {
    if (v === '' || v === null || v === undefined) return null;
    const n = parseFloat(v);
    return isNaN(n) ? null : n;
  };

  const computeBMI = () => {
    const h = parseNum(patientInfo.height_cm);
    const w = parseNum(patientInfo.weight_kg);
    if (h && w && h > 0) {
      return Math.round((w / ((h / 100) ** 2)) * 10) / 10;
    }
    return null;
  };

  // ---- Handlers ----

  const handlePatientChange = (field, value) => {
    setPatientInfo((p) => ({ ...p, [field]: value }));
  };

  const handleBodyCompChange = (region, field, value) => {
    setBodyComposition((prev) => ({
      ...prev,
      [region]: { ...prev[region], [field]: value },
    }));
  };

  const handleBoneChange = (field, value) => {
    setBoneDensity((prev) => ({ ...prev, [field]: value }));
  };

  const handleAdiposeChange = (field, value) => {
    setAdiposeIndices((prev) => ({ ...prev, [field]: value }));
  };

  const handleLeanChange = (field, value) => {
    setLeanIndices((prev) => ({ ...prev, [field]: value }));
  };

  const handleCalculate = () => {
    const adipose = {};
    for (const [k, v] of Object.entries(adiposeIndices)) {
      adipose[k] = parseNum(v);
    }
    const bone = {};
    for (const [k, v] of Object.entries(boneDensity)) {
      bone[k] = parseNum(v);
    }
    const lean = {};
    for (const [k, v] of Object.entries(leanIndices)) {
      lean[k] = parseNum(v);
    }
    const bmi = computeBMI();
    const result = calculateAllScores(adipose, bone, lean, bmi);
    setScores(result);
    setView('results');
  };

  const handleReset = () => {
    setView('upload');
    setFile(null);
    setError('');
    setPatientInfo({});
    setBodyComposition(() => {
      const init = {};
      BODY_REGIONS.forEach((r) => {
        init[r] = { fat_mass: '', lean_bmc: '', total_mass: '', pct_fat: '' };
      });
      return init;
    });
    setBoneDensity({ total_bmd: '', t_score: '', z_score: '' });
    setAdiposeIndices({
      total_body_pct_fat: '', fat_mass_height_sq: '',
      android_gynoid_ratio: '', pct_fat_trunk_legs: '',
      trunk_limb_ratio: '', vat_mass: '', vat_volume: '', vat_area: '',
    });
    setLeanIndices({ lean_height_sq: '', appen_lean_height_sq: '' });
    setScores(null);
    setSaveStatus('');
    fetchSavedResults();
  };

  const handleSave = async () => {
    setSaveStatus('saving');
    try {
      // Convert string values to numbers for storage
      const numBodyComp = {};
      for (const [region, fields] of Object.entries(bodyComposition)) {
        numBodyComp[region] = {};
        for (const [k, v] of Object.entries(fields)) {
          numBodyComp[region][k] = parseNum(v);
        }
      }
      const numBone = {};
      for (const [k, v] of Object.entries(boneDensity)) numBone[k] = parseNum(v);
      const numAdipose = {};
      for (const [k, v] of Object.entries(adiposeIndices)) numAdipose[k] = parseNum(v);
      const numLean = {};
      for (const [k, v] of Object.entries(leanIndices)) numLean[k] = parseNum(v);

      await apiService.dexaScans.saveResults({
        patient_info: patientInfo,
        body_composition: numBodyComp,
        bone_density: numBone,
        adipose_indices: numAdipose,
        lean_indices: numLean,
        scores,
      });
      setSaveStatus('saved');
      fetchSavedResults();
    } catch {
      setSaveStatus('error');
    }
  };

  const handleLoadSaved = async (resultId) => {
    try {
      const res = await apiService.dexaScans.getResultDetail(resultId);
      const data = res.data;
      setPatientInfo(data.patient_info || {});

      // Restore body composition
      if (data.body_composition) {
        const bc = {};
        BODY_REGIONS.forEach((r) => {
          const saved = data.body_composition[r] || {};
          bc[r] = {
            fat_mass: saved.fat_mass ?? '',
            lean_bmc: saved.lean_bmc ?? '',
            total_mass: saved.total_mass ?? '',
            pct_fat: saved.pct_fat ?? '',
          };
        });
        setBodyComposition(bc);
      }

      if (data.bone_density) {
        setBoneDensity({
          total_bmd: data.bone_density.total_bmd ?? '',
          t_score: data.bone_density.t_score ?? '',
          z_score: data.bone_density.z_score ?? '',
        });
      }

      if (data.adipose_indices) {
        const ai = {};
        for (const k of Object.keys(adiposeIndices)) {
          ai[k] = data.adipose_indices[k] ?? '';
        }
        setAdiposeIndices(ai);
      }

      if (data.lean_indices) {
        setLeanIndices({
          lean_height_sq: data.lean_indices.lean_height_sq ?? '',
          appen_lean_height_sq: data.lean_indices.appen_lean_height_sq ?? '',
        });
      }

      if (data.scores) {
        setScores(data.scores);
        setView('results');
      } else {
        setView('form');
      }
    } catch {
      // silently fail
    }
  };

  // ---- Render: Upload view ----

  const renderUpload = () => (
    <>
      <h1>DEXA Scan</h1>
      <p>Upload your DEXA scan PDF to extract values, or enter them manually for a personalized body composition assessment.</p>

      <div className="card upload-card">
        <div
          className={`drop-zone ${isDragging ? 'dragging' : ''}`}
          onDragOver={handleDragOver}
          onDragLeave={handleDragLeave}
          onDrop={handleDrop}
          onClick={() => fileInputRef.current?.click()}
        >
          <div className="drop-icon">
            <svg width="48" height="48" viewBox="0 0 24 24" fill="none" stroke="#d6336c" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
              <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z" />
              <polyline points="14 2 14 8 20 8" />
              <line x1="12" y1="18" x2="12" y2="12" />
              <line x1="9" y1="15" x2="12" y2="12" />
              <line x1="15" y1="15" x2="12" y2="12" />
            </svg>
          </div>
          <p className="drop-text">Drag & drop your DEXA scan PDF here</p>
          <p className="drop-subtext">or click to browse files</p>
          <input
            ref={fileInputRef}
            type="file"
            accept=".pdf"
            onChange={handleFileSelect}
            style={{ display: 'none' }}
          />
        </div>

        {file && (
          <div className="selected-file">
            <span className="file-name">{file.name}</span>
            <span className="file-size">({(file.size / 1024).toFixed(0)} KB)</span>
          </div>
        )}

        {error && <div className="error-msg">{error}</div>}

        <div className="upload-actions">
          <button
            className="btn btn-primary extract-btn"
            onClick={handleExtract}
            disabled={!file}
          >
            Extract DEXA Data
          </button>
          <button
            className="btn btn-secondary"
            onClick={handleManualEntry}
            style={{ marginTop: '0.75rem' }}
          >
            Enter Values Manually
          </button>
        </div>
      </div>

      {/* Past Results */}
      {savedResults.length > 0 && (
        <div className="card past-results-card">
          <h3>Past DEXA Scans</h3>
          <div className="past-results-list">
            {savedResults.map((r) => (
              <button
                key={r.id}
                className="past-result-item"
                onClick={() => handleLoadSaved(r.id)}
              >
                <div className="past-result-info">
                  <span className="past-result-name">
                    {r.patient_name || r.facility_name || 'DEXA Scan'}
                  </span>
                  <span className="past-result-meta">
                    {r.facility_name && `${r.facility_name} \u00b7 `}
                    {r.created_at ? new Date(r.created_at).toLocaleDateString() : ''}
                  </span>
                </div>
                {r.overall_score !== null && r.overall_score !== undefined && (
                  <span className={`score-badge badge-${getStatus(r.overall_score)}`}>
                    {r.overall_score}
                  </span>
                )}
              </button>
            ))}
          </div>
        </div>
      )}
    </>
  );

  // ---- Render: Loading view ----

  const renderLoading = () => (
    <div className="loading-container">
      <div className="spinner" />
      <h2>Analyzing your DEXA scan...</h2>
      <p>This may take 15-30 seconds as we extract your scan values.</p>
    </div>
  );

  // ---- Render: Form view ----

  const renderForm = () => {
    const bmi = computeBMI();

    return (
      <>
        <h1>DEXA Scan</h1>
        <p>Enter your DEXA scan values to get a personalized body composition assessment based on women's-health-optimized ranges.</p>

        {/* Patient Info */}
        <div className="card dexa-section-card">
          <h3>Patient Information</h3>
          <div className="patient-fields">
            {[
              { key: 'name', label: 'Name' },
              { key: 'date_of_birth', label: 'Date of Birth' },
              { key: 'sex', label: 'Sex' },
              { key: 'height_cm', label: 'Height (cm)', type: 'number' },
              { key: 'weight_kg', label: 'Weight (kg)', type: 'number' },
              { key: 'scan_date', label: 'Scan Date' },
              { key: 'facility', label: 'Facility' },
            ].map((f) => (
              <div key={f.key} className="patient-field">
                <label>{f.label}</label>
                <input
                  type={f.type || 'text'}
                  step={f.type === 'number' ? 'any' : undefined}
                  value={patientInfo[f.key] || ''}
                  onChange={(e) => handlePatientChange(f.key, e.target.value)}
                />
              </div>
            ))}
          </div>
          {bmi && (
            <div className="bmi-display">
              Calculated BMI: <strong>{bmi}</strong>
              <span className={`badge badge-${getStatus(scoreTest('BMI', bmi))}`} style={{ marginLeft: '0.5rem' }}>
                {getStatus(scoreTest('BMI', bmi))}
              </span>
            </div>
          )}
        </div>

        {/* Body Composition Table */}
        <div className="card dexa-section-card">
          <h3>Body Composition</h3>
          <div className="body-comp-table">
            <div className="body-comp-header">
              <span className="bc-region">Region</span>
              {BODY_FIELDS.map((f) => (
                <span key={f.key} className="bc-cell">{f.label}</span>
              ))}
            </div>
            {BODY_REGIONS.map((region) => (
              <div key={region} className={`body-comp-row ${region === 'Total' ? 'body-comp-total' : ''}`}>
                <span className="bc-region">{region}</span>
                {BODY_FIELDS.map((f) => (
                  <input
                    key={f.key}
                    className="bc-cell"
                    type="number"
                    step="any"
                    value={bodyComposition[region]?.[f.key] || ''}
                    onChange={(e) => handleBodyCompChange(region, f.key, e.target.value)}
                    placeholder="—"
                  />
                ))}
              </div>
            ))}
          </div>
        </div>

        {/* Bone Density */}
        <div className="card dexa-section-card">
          <h3>Bone Density</h3>
          <div className="dexa-fields-row">
            {[
              { key: 'total_bmd', label: 'Total BMD (g/cm\u00B2)' },
              { key: 't_score', label: 'T-Score' },
              { key: 'z_score', label: 'Z-Score' },
            ].map((f) => (
              <div key={f.key} className="dexa-field">
                <label>{f.label}</label>
                <input
                  type="number"
                  step="any"
                  value={boneDensity[f.key]}
                  onChange={(e) => handleBoneChange(f.key, e.target.value)}
                />
                <span className="dexa-optimal">Optimal: {getOptimalDisplay(f.label.split(' (')[0].replace('Total BMD', 'Total BMD'))}</span>
              </div>
            ))}
          </div>
        </div>

        {/* Adipose Indices */}
        <div className="card dexa-section-card">
          <h3>Adipose Indices</h3>
          <div className="dexa-fields-row">
            {[
              { key: 'total_body_pct_fat', label: 'Total Body % Fat', optimal: 'Total Body % Fat' },
              { key: 'fat_mass_height_sq', label: 'Fat Mass/Height\u00B2 (kg/m\u00B2)', optimal: 'Fat Mass/Height\u00B2' },
              { key: 'android_gynoid_ratio', label: 'Android/Gynoid Ratio', optimal: 'Android/Gynoid Ratio' },
              { key: 'pct_fat_trunk_legs', label: '% Fat Trunk/% Fat Legs', optimal: '% Fat Trunk/% Fat Legs' },
              { key: 'trunk_limb_ratio', label: 'Trunk/Limb Fat Mass Ratio', optimal: 'Trunk/Limb Fat Mass Ratio' },
              { key: 'vat_mass', label: 'Est. VAT Mass (g)', optimal: 'VAT Mass' },
              { key: 'vat_volume', label: 'Est. VAT Volume (cm\u00B3)', optimal: null },
              { key: 'vat_area', label: 'Est. VAT Area (cm\u00B2)', optimal: 'VAT Area' },
            ].map((f) => (
              <div key={f.key} className="dexa-field">
                <label>{f.label}</label>
                <input
                  type="number"
                  step="any"
                  value={adiposeIndices[f.key]}
                  onChange={(e) => handleAdiposeChange(f.key, e.target.value)}
                />
                {f.optimal && <span className="dexa-optimal">Optimal: {getOptimalDisplay(f.optimal)}</span>}
              </div>
            ))}
          </div>
        </div>

        {/* Lean Indices */}
        <div className="card dexa-section-card">
          <h3>Lean Mass Indices</h3>
          <div className="dexa-fields-row">
            {[
              { key: 'lean_height_sq', label: 'Lean/Height\u00B2 (kg/m\u00B2)', optimal: 'Lean/Height\u00B2' },
              { key: 'appen_lean_height_sq', label: 'Appen. Lean/Height\u00B2 (kg/m\u00B2)', optimal: 'Appen. Lean/Height\u00B2' },
            ].map((f) => (
              <div key={f.key} className="dexa-field">
                <label>{f.label}</label>
                <input
                  type="number"
                  step="any"
                  value={leanIndices[f.key]}
                  onChange={(e) => handleLeanChange(f.key, e.target.value)}
                />
                <span className="dexa-optimal">Optimal: {getOptimalDisplay(f.optimal)}</span>
              </div>
            ))}
          </div>
        </div>

        <div className="form-actions-bar">
          <button className="btn btn-secondary" onClick={handleReset}>
            Upload New Scan
          </button>
          <button className="btn btn-primary" onClick={handleCalculate}>
            Calculate Scores
          </button>
        </div>
      </>
    );
  };

  // ---- Render: Results / dashboard view ----

  const renderResults = () => {
    if (!scores) return null;
    const { categories, overall, overallStatus } = scores;
    const bmi = computeBMI();

    return (
      <>
        {/* Overall score header */}
        <div className="results-header">
          <div className={`overall-score score-${overallStatus}`}>
            <svg viewBox="0 0 120 120" className="score-ring">
              <circle cx="60" cy="60" r="54" fill="none" stroke="#e5e7eb" strokeWidth="8" />
              <circle
                cx="60" cy="60" r="54"
                fill="none"
                strokeWidth="8"
                strokeLinecap="round"
                strokeDasharray={`${(overall / 100) * 339.3} 339.3`}
                transform="rotate(-90 60 60)"
                className="score-ring-fill"
              />
            </svg>
            <div className="score-number">{overall ?? '\u2014'}</div>
            <div className="score-label">Overall DEXA Score</div>
          </div>
          <div className="results-summary">
            <h1>DEXA Assessment</h1>
            {patientInfo.name && <p><strong>Patient:</strong> {patientInfo.name}</p>}
            {patientInfo.scan_date && <p><strong>Scan Date:</strong> {patientInfo.scan_date}</p>}
            {patientInfo.facility && <p><strong>Facility:</strong> {patientInfo.facility}</p>}
            {bmi && <p><strong>BMI:</strong> {bmi}</p>}
          </div>
        </div>

        {/* Category cards */}
        <div className="category-grid">
          {CATEGORY_ORDER.map((cat) => {
            const data = categories[cat];
            if (!data) return null;
            const info = CATEGORY_INFO[cat] || { name: cat };

            return (
              <div key={cat} className="card result-card">
                <div className="result-card-header">
                  <h3>{info.name}</h3>
                  {data.score !== null && (
                    <span className={`score-badge badge-${data.status}`}>
                      {data.score}
                    </span>
                  )}
                </div>

                {data.score !== null && (
                  <div className="score-bar-container">
                    <div
                      className={`score-bar bar-${data.status}`}
                      style={{ width: `${data.score}%` }}
                    />
                  </div>
                )}

                <div className="result-tests">
                  {data.tests.map((t, i) => (
                    <div key={i} className="result-test-row">
                      <span className="rt-name">{t.name}</span>
                      <span className="rt-value">
                        {t.value !== null ? t.value : '\u2014'}
                      </span>
                      <span className={`rt-status badge badge-${t.status}`}>
                        {t.status === 'unknown' ? '\u2014' : t.status}
                      </span>
                    </div>
                  ))}
                </div>

                {info.context && (
                  <div className="context-note">
                    <strong>Open Health Note:</strong> {info.context}
                  </div>
                )}
              </div>
            );
          })}
        </div>

        {/* Disclaimer */}
        <div className="card disclaimer-card">
          <strong>Medical Disclaimer:</strong> This assessment uses women's-health-optimized
          ranges for educational purposes. It is not a medical diagnosis. Always consult
          your healthcare provider for interpretation of DEXA scan results.
        </div>

        <div className="form-actions-bar">
          <button className="btn btn-secondary" onClick={() => setView('form')}>
            Back to Edit
          </button>
          <div className="results-actions-right">
            <button
              className={`btn btn-save ${saveStatus === 'saved' ? 'btn-saved' : ''}`}
              onClick={handleSave}
              disabled={saveStatus === 'saving' || saveStatus === 'saved'}
            >
              {saveStatus === 'saving' && 'Saving...'}
              {saveStatus === 'saved' && 'Saved'}
              {saveStatus === 'error' && 'Retry Save'}
              {saveStatus === '' && 'Save to Account'}
            </button>
            <button className="btn btn-primary" onClick={handleReset}>
              Analyze Another Scan
            </button>
          </div>
        </div>
      </>
    );
  };

  // ---- Auth gate ----

  if (!user) {
    return (
      <div className="page dexa-scans-page">
        <h1>DEXA Scans</h1>
        <p>Please log in to access the DEXA scan analyzer.</p>
        <Link to="/login" className="btn btn-primary">Go to Login</Link>
      </div>
    );
  }

  // ---- Main render ----

  return (
    <div className="page dexa-scans-page">
      {fromOnboarding && (
        <div style={{ background: '#fff0f6', border: '1px solid #fcc2d7', borderRadius: 8, padding: '10px 16px', marginBottom: 16, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <span style={{ fontSize: 14 }}>You're setting up your account.</span>
          <Link to="/onboarding" style={{ color: '#d6336c', fontWeight: 600, fontSize: 14, textDecoration: 'none' }}>← Back to Setup</Link>
        </div>
      )}
      {view === 'upload' && renderUpload()}
      {view === 'extracting' && renderLoading()}
      {view === 'form' && renderForm()}
      {view === 'results' && renderResults()}
    </div>
  );
}

export default DexaScans;
